import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './styles/SubscriptionPlans.css';

const SubscriptionPlans = () => {
  const { t, language } = useLanguage();
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  // Sæt standardvaluta baseret på sprog
  const getDefaultCurrency = () => {
    return language === 'danish' ? 'DKK' : 'USD';
  };
  
  const [currency, setCurrency] = useState(getDefaultCurrency());
  const [basicQuantity, setBasicQuantity] = useState(1);

  // Opdater valuta når sproget ændres
  useEffect(() => {
    setCurrency(getDefaultCurrency());
  }, [language]);

  // Kun Basic plan er tilgængelig
  const plans = {
    basic: {
      name: 'Basic',
      monthly: { DKK: 1490, USD: 229 },
      yearly: { DKK: 14900, USD: 2290 },
      color: 'basic'
    }
  };

  const handleQuantityChange = (plan, value) => {
    const newValue = Math.max(1, Math.min(50, parseInt(value) || 1));
    if (plan === 'basic') {
      setBasicQuantity(newValue);
    }
  };

  const calculateTotal = () => {
    const basicPrice = plans.basic[billingCycle][currency];
    return basicPrice * basicQuantity;
  };

  const getCurrencySymbol = () => {
    return currency === 'DKK' ? 'kr.' : '$';
  };

  const handleCheckout = async () => {
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: 'basic',
          billingCycle,
          currency: currency.toLowerCase(),
          quantity: basicQuantity,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const session = await response.json();
      
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert(t('subscriptionPlans', 'errors.checkoutError'));
    }
  };

  const renderQuantitySelector = (planId) => (
    <div className="sub-quantity-selector">
      <label className="sub-quantity-label">{t('subscriptionPlans', 'quantity.label')}</label>
      <div className="sub-quantity-controls">
        <button 
          type="button"
          className="sub-quantity-btn sub-quantity-btn-minus"
          onClick={() => handleQuantityChange(planId, (planId === 'basic' ? basicQuantity : 1) - 1)}
          disabled={(planId === 'basic' ? basicQuantity : 1) <= 1}
        >
          -
        </button>
        <input
          type="number"
          className="sub-quantity-input"
          min="1"
          max="50"
          value={planId === 'basic' ? basicQuantity : 1}
          onChange={(e) => handleQuantityChange(planId, e.target.value)}
        />
        <button 
          type="button"
          className="sub-quantity-btn sub-quantity-btn-plus"
          onClick={() => handleQuantityChange(planId, (planId === 'basic' ? basicQuantity : 1) + 1)}
          disabled={(planId === 'basic' ? basicQuantity : 1) >= 50}
        >
          +
        </button>
      </div>
      <div className="sub-seats-info">
        <i className="fas fa-info-circle"></i>
        <span>{t('subscriptionPlans', 'quantity.seatsInfo')}</span>
      </div>
    </div>
  );

  return (
    <div className="sub-container">
      <div className="sub-header">
        <h1 className="sub-title">{t('subscriptionPlans', 'header.title')}</h1>
        <p className="sub-description">
          {t('subscriptionPlans', 'header.description')}
        </p>
      </div>

      <div className="sub-trial-banner">
        <i className="fas fa-gift"></i>
        <div>
          <h3>{t('subscriptionPlans', 'trial.title')}</h3>
          <p>{t('subscriptionPlans', 'trial.subtitle')}</p>
        </div>
      </div>

      <div className="sub-controls-container">
        <div className="sub-billing-toggle">
          <button 
            className={`sub-billing-btn ${billingCycle === 'monthly' ? 'sub-billing-btn-active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            {t('subscriptionPlans', 'billing.monthly')}
          </button>
          <button 
            className={`sub-billing-btn ${billingCycle === 'yearly' ? 'sub-billing-btn-active' : ''}`}
            onClick={() => setBillingCycle('yearly')}
          >
            {t('subscriptionPlans', 'billing.yearly')}
            {billingCycle === 'yearly' && <span className="sub-savings-badge">{t('subscriptionPlans', 'billing.savingsLabel')}</span>}
          </button>
        </div>

        <div className="sub-currency-toggle">
          <button 
            className={`sub-currency-btn ${currency === 'DKK' ? 'sub-currency-btn-active' : ''}`}
            onClick={() => setCurrency('DKK')}
          >
            {t('subscriptionPlans', 'currency.dkk')}
          </button>
          <button 
            className={`sub-currency-btn ${currency === 'USD' ? 'sub-currency-btn-active' : ''}`}
            onClick={() => setCurrency('USD')}
          >
            {t('subscriptionPlans', 'currency.usd')}
          </button>
        </div>
      </div>

      <div className="sub-plan-container">
        <div className={`sub-plan-card sub-plan-card-${plans.basic.color}`}>
          <div className="sub-plan-header">
            <h2 className="sub-plan-name">{t('subscriptionPlans', 'plans.basic')}</h2>
            <div className="sub-price">
              <span className="sub-price-amount">{plans.basic[billingCycle][currency]}</span>
              <span className="sub-price-currency">{getCurrencySymbol()}</span>
              <span className="sub-price-interval">/{t('subscriptionPlans', `pricing.${billingCycle}`)}</span>
            </div>
          </div>

          {renderQuantitySelector('basic')}
        </div>
      </div>

      <div className="sub-summary">
        <div className="sub-total-amount">
          {t('subscriptionPlans', 'summary.total')} <span className="sub-total-price">{calculateTotal()}</span> {getCurrencySymbol()} / {t('subscriptionPlans', `pricing.${billingCycle}`)}
        </div>
        <button className="sub-checkout-button" onClick={handleCheckout}>
          <i className="fas fa-credit-card"></i>
          {t('subscriptionPlans', 'summary.checkoutButton')}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionPlans;